/**
 * HierarchyKindIcon — the leading glyph for a row in a hierarchical
 * catalog tree (Designer `traits.hierarchical`): a filled folder for
 * groups (open when expanded) and a label glyph for catalog elements
 * (not a document — those belong to documents, not справочники).
 *
 * Optional `locked` overlays a small PredefinedLock badge on the glyph
 * (not after the row label).
 *
 * Lives in `shared/` (not `@/runtime`) so Designer's Predefined tab
 * (which renders the same hierarchy visuals for a preview list) can
 * import it without crossing the designer -> runtime module boundary.
 */
import React from 'react'
import Box from '@mui/material/Box'
import Folder from '@mui/icons-material/Folder'
import FolderOpen from '@mui/icons-material/FolderOpen'
import Label from '@mui/icons-material/Label'
import { PredefinedLock } from '@/shared/PredefinedLock'

export interface HierarchyKindIconProps {
  kind: 'group' | 'element'
  expanded?: boolean
  /** Predefined row — lock badge sits on the kind glyph. */
  locked?: boolean
}

export const HierarchyKindIcon: React.FC<HierarchyKindIconProps> = ({
  kind,
  expanded,
  locked = false,
}) => {
  let glyph: React.ReactNode
  if (kind === 'group') {
    const Icon = expanded ? FolderOpen : Folder
    glyph = (
      <Icon
        data-testid="hierarchy-kind-group"
        fontSize="small"
        color="primary"
        sx={{ fontSize: 18 }}
      />
    )
  } else {
    glyph = (
      <Label
        data-testid="hierarchy-kind-element"
        fontSize="small"
        sx={{ fontSize: 18, color: 'text.secondary' }}
      />
    )
  }

  if (!locked) return <>{glyph}</>

  return (
    <Box
      data-testid="hierarchy-kind-locked"
      sx={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        lineHeight: 0,
      }}
    >
      {glyph}
      <Box
        sx={{
          position: 'absolute',
          right: -4,
          bottom: -3,
          lineHeight: 0,
          bgcolor: 'background.paper',
          borderRadius: '50%',
          display: 'inline-flex',
          '& .MuiSvgIcon-root': { fontSize: 11 },
        }}
      >
        <PredefinedLock />
      </Box>
    </Box>
  )
}
